/**
 * Timezone Utility Functions for Customer App
 * Handles date/time formatting based on customer's timezone
 */

/**
 * Get customer timezone from profile or default
 * @returns {string} IANA timezone string (e.g., 'America/New_York')
 */
export const getCustomerTimezone = () => {
  if (typeof window === 'undefined') return 'Australia/Sydney';
  
  try {
    // Try to get from user profile in localStorage
    const userStr = localStorage.getItem('user') || localStorage.getItem('customerUser');
    if (userStr) {
      const user = JSON.parse(userStr);
      if (user?.preferences?.timezone) {
        return user.preferences.timezone;
      }
      if (user?.location?.country) {
        // Fallback to country-based timezone
        return getTimezoneForCountry(user.location.country);
      }
    }
    
    // Try to get from country code in localStorage
    const countryCode = localStorage.getItem('countryCode');
    if (countryCode) {
      return getTimezoneForCountry(countryCode);
    }
    
    // Fallback to browser timezone
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'Australia/Sydney'; // Ultimate fallback
    }
  } catch (error) {
    console.warn('Error getting customer timezone:', error);
    return 'Australia/Sydney';
  }
};

/**
 * Get timezone for a country code
 * @param {string} countryCode - Country code (AU, IN, US, etc.)
 * @returns {string} IANA timezone string
 */
export const getTimezoneForCountry = (countryCode) => {
  const timezoneMap = {
    'AU': 'Australia/Sydney',
    'IN': 'Asia/Kolkata',
    'AE': 'Asia/Dubai',
    'US': 'America/New_York',
    'SG': 'Asia/Singapore',
    'GB': 'Europe/London',
    'MY': 'Asia/Kuala_Lumpur'
  };
  
  return timezoneMap[countryCode?.toUpperCase()] || 'Australia/Sydney';
};

/**
 * Format date in customer's timezone
 * @param {Date|string} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateInTimezone = (date, options = {}) => {
  const timezone = getCustomerTimezone();
  const dateObj = date instanceof Date ? date : new Date(date);
  
  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone,
    ...options
  };
  
  return dateObj.toLocaleDateString('en-US', defaultOptions);
};

/**
 * Format time in customer's timezone
 * @param {Date|string} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted time string
 */
export const formatTimeInTimezone = (date, options = {}) => {
  const timezone = getCustomerTimezone();
  const dateObj = date instanceof Date ? date : new Date(date);
  
  const defaultOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
    ...options
  };
  
  return dateObj.toLocaleTimeString('en-US', defaultOptions);
};

/**
 * Format date and time in customer's timezone
 * @param {Date|string} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date-time string
 */
export const formatDateTimeInTimezone = (date, options = {}) => {
  const timezone = getCustomerTimezone();
  const dateObj = date instanceof Date ? date : new Date(date);
  
  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
    ...options
  };
  
  return dateObj.toLocaleString('en-US', defaultOptions);
};

/**
 * Get current date in customer's timezone
 * @returns {Date} Date object representing current time in customer's timezone
 */
export const getCurrentDateInTimezone = () => {
  const timezone = getCustomerTimezone();
  const now = new Date();
  
  // Convert to timezone string and parse back
  const timezoneString = now.toLocaleString('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  // Parse the formatted string: "MM/DD/YYYY, HH:mm:ss"
  const [datePart, timePart] = timezoneString.split(', ');
  const [month, day, year] = datePart.split('/');
  const [hours, minutes, seconds] = timePart.split(':');
  
  // Create new Date object with timezone time components
  return new Date(year, month - 1, day, hours, minutes, seconds);
};

/**
 * Convert UTC date to customer's timezone Date object
 * @param {Date|string} utcDate - UTC date
 * @returns {Date} Date object in customer's timezone
 */
export const convertUTCToCustomerTimezone = (utcDate) => {
  const timezone = getCustomerTimezone();
  const dateObj = utcDate instanceof Date ? utcDate : new Date(utcDate);
  
  // Get the timezone offset
  const utcTime = dateObj.getTime() + (dateObj.getTimezoneOffset() * 60000);
  
  // Convert to timezone
  const timezoneDate = new Date(utcTime);
  const timezoneString = timezoneDate.toLocaleString('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  // Parse and create new Date
  const [datePart, timePart] = timezoneString.split(', ');
  const [month, day, year] = datePart.split('/');
  const [hours, minutes, seconds] = timePart.split(':');
  
  return new Date(year, month - 1, day, hours, minutes, seconds);
};

/**
 * Format date for display (e.g., "Monday, January 15, 2024")
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateLong = (date) => {
  const timezone = getCustomerTimezone();
  const dateObj = date instanceof Date ? date : new Date(date);
  
  return dateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: timezone
  });
};

/**
 * Format date for display (e.g., "Jan 15, 2024")
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateShort = (date) => {
  const timezone = getCustomerTimezone();
  const dateObj = date instanceof Date ? date : new Date(date);
  
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: timezone
  });
};

/**
 * Format time for display (e.g., "2:30 PM")
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted time string
 */
export const formatTime = (date) => {
  return formatTimeInTimezone(date);
};

/**
 * Format date and time together (e.g., "Jan 15, 2024 at 2:30 PM")
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date-time string
 */
export const formatDateTime = (date) => {
  const timezone = getCustomerTimezone();
  const dateObj = date instanceof Date ? date : new Date(date);
  
  const dateStr = formatDateShort(date);
  const timeStr = formatTimeInTimezone(date);
  
  return `${dateStr} at ${timeStr}`;
};

/**
 * Check if a date is today in customer's timezone
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  const timezone = getCustomerTimezone();
  const dateObj = date instanceof Date ? date : new Date(date);
  const today = getCurrentDateInTimezone();
  
  // Compare date parts only
  const dateOnly = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  
  return dateOnly.getTime() === todayOnly.getTime();
};

/**
 * Check if a date is in the past in customer's timezone
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is in the past
 */
export const isPastDate = (date) => {
  const today = getCurrentDateInTimezone();
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Compare date parts only
  const dateOnly = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  
  return dateOnly.getTime() < todayOnly.getTime();
};

/**
 * Check if a date is in the future in customer's timezone
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is in the future
 */
export const isFutureDate = (date) => {
  const today = getCurrentDateInTimezone();
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Compare date parts only
  const dateOnly = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  
  return dateOnly.getTime() > todayOnly.getTime();
};